import { formatJstDateTime, instantFromIso } from "@nijiurachan/js/util/time";

export interface ThreadLimits {
  activeThreads: number;
  durationHours: number;
  inputMode: ThreadDurationInputMode;
  minimumMinutes: number;
  timeZone: string;
  nextBoundaryAt: string | null;
  allowR18ThreadCreation: boolean;
}

export type ThreadDurationInputMode = "hours_minutes" | "days_hours_minutes";

export const THREAD_LIMITS_FALLBACK: ThreadLimits = {
  activeThreads: 0,
  durationHours: 12,
  inputMode: "hours_minutes",
  minimumMinutes: 30,
  timeZone: "Asia/Tokyo",
  nextBoundaryAt: null,
  allowR18ThreadCreation: true,
};

export const THREAD_LIMITS_REFETCH_MIN_MS = 1_000;
export const THREAD_LIMITS_CONFIG_REFETCH_MS = 30_000;
const THREAD_LIMITS_REFETCH_MAX_MS: number = 26 * 60 * 60 * 1_000;

/** 「日」「時間」「分」3フィールドの生入力値（未入力は空文字列）。 */
export interface ThreadDurationParts {
  days: string;
  hours: string;
  minutes: string;
}

export const EMPTY_THREAD_DURATION_PARTS: ThreadDurationParts = {
  days: "",
  hours: "",
  minutes: "",
};

function usesDayDurationInput(
  inputMode: ThreadDurationInputMode | undefined,
): boolean {
  return inputMode === "days_hours_minutes";
}

export function formatDurationHours(
  durationHours: number,
  inputMode: ThreadDurationInputMode = "hours_minutes",
): string {
  const durationMinutes = Math.round(durationHours * 60);
  const totalHours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  if (!usesDayDurationInput(inputMode)) {
    return `${totalHours}:${String(minutes).padStart(2, "0")}`;
  }
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return `${days}:${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function isEmptyDurationParts(parts: ThreadDurationParts): boolean {
  return (
    parts.days.trim() === "" &&
    parts.hours.trim() === "" &&
    parts.minutes.trim() === ""
  );
}

/** 既定値は上限の半分とし、構成上の最小値を下回る場合は最小値へ補正する。 */
export function threadDurationDefaultParts(
  limits: ThreadLimits,
): ThreadDurationParts {
  const halfDurationMinutes = Math.max(
    Math.floor((limits.durationHours * 60) / 2),
    limits.minimumMinutes,
  );
  const halfDurationHours = Math.floor(halfDurationMinutes / 60);
  const usesDays = usesDayDurationInput(limits.inputMode);
  return {
    days: usesDays ? String(Math.floor(halfDurationHours / 24)) : "",
    hours: String(usesDays ? halfDurationHours % 24 : halfDurationHours),
    minutes: String(halfDurationMinutes % 60),
  };
}

export function resolveThreadDurationParts(
  currentParts: ThreadDurationParts,
  limits: ThreadLimits | undefined,
  wasEdited: boolean,
): ThreadDurationParts {
  if (wasEdited || limits === undefined) return currentParts;
  if (!isEmptyDurationParts(currentParts)) return currentParts;
  return threadDurationDefaultParts(limits);
}

export function threadDurationHint(limits: ThreadLimits): string {
  if (usesDayDurationInput(limits.inputMode)) {
    const durationMinutes = Math.round(limits.durationHours * 60);
    const totalHours = Math.floor(durationMinutes / 60);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = durationMinutes % 60;
    return `現在の上限 ${days}日:${String(hours).padStart(2, "0")}時間:${String(minutes).padStart(2, "0")}分`;
  }
  return `現在の上限 ${formatDurationHours(limits.durationHours, limits.inputMode)}`;
}

export function threadDurationDetails(
  limits: ThreadLimits,
  isDegraded: boolean = false,
): string {
  const nextBoundary = formatThreadLimitBoundary(limits);
  const boundaryHint = nextBoundary
    ? `、次の切替 ${nextBoundary} ${limits.timeZone}`
    : "";
  const inputModeHint = usesDayDurationInput(limits.inputMode)
    ? "日:時間:分"
    : "時間:分";
  const details = `最小${limits.minimumMinutes}分、空欄は${isDegraded ? "サーバー上限" : "上限"}${boundaryHint}、入力形式${inputModeHint}`;
  return isDegraded
    ? `${details}\nAPI取得に失敗したため、直近値またはフォールバック値を表示しています。`
    : details;
}

export function formatThreadLimitBoundary(limits: ThreadLimits): string | null {
  if (limits.nextBoundaryAt === null) return null;
  const instant = instantFromIso(limits.nextBoundaryAt);
  if (!instant) return null;
  try {
    const boundary = instant.toZonedDateTimeISO(limits.timeZone);
    return `${String(boundary.hour).padStart(2, "0")}:${String(boundary.minute).padStart(2, "0")}`;
  } catch {
    return null;
  }
}

export function threadLimitsRefetchInterval(
  limits: ThreadLimits | undefined,
  now: Temporal.Instant,
): number | false {
  if (!limits?.nextBoundaryAt) return THREAD_LIMITS_CONFIG_REFETCH_MS;
  const boundary = instantFromIso(limits.nextBoundaryAt);
  if (!boundary) return THREAD_LIMITS_CONFIG_REFETCH_MS;
  const delay = boundary.epochMilliseconds - now.epochMilliseconds;
  if (
    !Number.isFinite(delay) ||
    delay <= 0 ||
    delay > THREAD_LIMITS_REFETCH_MAX_MS
  ) {
    return THREAD_LIMITS_CONFIG_REFETCH_MS;
  }
  return Math.min(
    THREAD_LIMITS_CONFIG_REFETCH_MS,
    Math.max(
      THREAD_LIMITS_REFETCH_MIN_MS,
      delay + THREAD_LIMITS_REFETCH_MIN_MS,
    ),
  );
}

/** 未入力の数値フィールドは0として扱う。 */
function parseDurationField(rawValue: string): number | null {
  const raw = rawValue.trim();
  if (raw === "") return 0;
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) ? value : null;
}

/** 日入力を使わない上限へ切り替える際、日数を総時間へ畳み込む。 */
export function collapseThreadDurationDays(
  parts: ThreadDurationParts,
): ThreadDurationParts {
  if (parts.days.trim() === "") return parts;
  const days = parseDurationField(parts.days);
  const hours = parseDurationField(parts.hours);
  if (days === null || hours === null) return { ...parts, days: "" };
  const totalHours = days * 24 + hours;
  if (!Number.isSafeInteger(totalHours)) return { ...parts, days: "" };
  return {
    days: "",
    hours: String(totalHours),
    minutes: parts.minutes,
  };
}

/** 総時間へ畳み込まれた入力を、日入力で扱える日数と0〜23時間へ戻す。 */
export function normalizeThreadDurationDays(
  parts: ThreadDurationParts,
): ThreadDurationParts {
  if (parts.days.trim() !== "") return parts;
  const hours = parseDurationField(parts.hours);
  if (hours === null || hours < 24) return parts;
  return {
    days: String(Math.floor(hours / 24)),
    hours: String(hours % 24),
    minutes: parts.minutes,
  };
}

export function validateThreadDurationParts(
  parts: ThreadDurationParts,
  maximumHours: number,
  minimumMinutes: number,
  inputMode: ThreadDurationInputMode = "hours_minutes",
): string | null {
  if (isEmptyDurationParts(parts)) return null;
  const days = parseDurationField(parts.days);
  const hours = parseDurationField(parts.hours);
  const minutes = parseDurationField(parts.minutes);
  if (days === null || hours === null || minutes === null) {
    return "保持期間は0以上の整数で入力してください";
  }
  if (minutes >= 60) {
    return "保持期間の分は0〜59で入力してください";
  }
  if (usesDayDurationInput(inputMode) && hours >= 24) {
    return "保持期間の時間は0〜23で入力してください";
  }
  const totalMinutes = days * MINUTES_PER_DAY + hours * 60 + minutes;
  if (totalMinutes < minimumMinutes) {
    return `保持期間は${minimumMinutes}分以上で指定してください`;
  }
  if (totalMinutes > Math.round(maximumHours * 60)) {
    return `保持期間は${formatDurationHours(maximumHours, inputMode)}以内で指定してください`;
  }
  return null;
}

/**
 * 送信ペイロードの duration 文字列（従来と同一のワイヤ契約）へ変換する。
 * 3フィールド空欄は空文字列（サーバー上限を意味する）のまま送る。
 * バリデーション済みの入力を前提とし、H:M形式（分は2桁）で組み立てる。
 *
 * 契約: 呼び出し前に validateThreadDurationParts が null（エラーなし）を
 * 返していることが前提。不正な値（非数値・負数等）は parseDurationField が
 * null を返すため `?? 0` で無音に0へ丸められる（例外を投げない）。
 * バリデーションを経由せずに不正な parts を渡すと、ユーザーに提示していた
 * 値と異なる保持期間が無警告で送信される。この丸めの回避（例外化）は
 * 本関数のスコープ外とする。
 */
export function formatThreadDurationPayload(
  parts: ThreadDurationParts,
): string {
  if (isEmptyDurationParts(parts)) return "";
  const days = parseDurationField(parts.days) ?? 0;
  const hours = parseDurationField(parts.hours) ?? 0;
  const minutes = parseDurationField(parts.minutes) ?? 0;
  return `${days * 24 + hours}:${String(minutes).padStart(2, "0")}`;
}

export function formatThreadExpiry(
  expiresAt: string | null | undefined,
  isPermanent: boolean | undefined,
): string {
  if (isPermanent) return "永久スレッド";
  if (!expiresAt) return "期限未設定";
  const instant = instantFromIso(expiresAt);
  if (!instant) return "期限不明";
  return `期限 ${formatJstDateTime(instant, {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

const MILLISECONDS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY: number = 24 * MINUTES_PER_HOUR;

/**
 * 「あと{残り時間}」の残り時間部分。秒は切り捨てる。
 * 24時間以上は「N日N時間」へ丸め、「50時間10分」のような読みにくい表記にしない。
 */
function formatRemainingDuration(remainingMilliseconds: number): string {
  const totalMinutes = Math.floor(
    remainingMilliseconds / MILLISECONDS_PER_MINUTE,
  );
  if (totalMinutes < 1) return "1分未満";
  const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const hours = Math.floor((totalMinutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  if (days > 0) return `${days}日${hours}時間`;
  if (hours > 0) return `${hours}時間${minutes}分`;
  return `${minutes}分`;
}

const THREAD_EXPIRED_TEXT = "スレッドが落ちています";

/** スレッド最下部の寿命表記。落ちている場合は警告色で出すため状態を添える。 */
interface ThreadLifetime {
  text: string; // 表示文言：「{日時} 頃落ちます(あと{残り時間})」か落ちた旨
  isEnded: boolean; // 落ちたか：呼び出し側が警告色へ切り替える判定に使う
}

/** 落ちたかどうかの判定に使う、スレッドの状態フラグ。 */
interface ThreadLifetimeFlags {
  isPermanent?: boolean | undefined; // 永久スレッドフラグ：寿命表記そのものを出さない
  isArchived?: boolean | undefined; // 過去ログ化済フラグ：期限計算より優先して落ちた扱い
}

/**
 * スレッド最下部の寿命表記「{日時} 頃落ちます(あと{残り時間})」を組み立てる。
 * 日時はJST M/D HH:MM。
 * 過去ログ化済み、または期限を過ぎている場合は落ちた旨の表記へ切り替える。
 *
 * 永久スレッド・期限未設定・解釈できないISO文字列では、誤った寿命を出すより
 * 何も出さない方が安全なため null を返す（呼び出し側が非表示にする）。
 */
export function formatThreadLifetime(
  expiresAt: string | null | undefined,
  now: Temporal.Instant,
  flags: ThreadLifetimeFlags = {},
): ThreadLifetime | null {
  if (flags.isArchived) return { text: THREAD_EXPIRED_TEXT, isEnded: true };
  if (flags.isPermanent) return null;
  if (!expiresAt) return null;
  const instant = instantFromIso(expiresAt);
  if (!instant) return null;
  const remaining = instant.epochMilliseconds - now.epochMilliseconds;
  if (remaining <= 0) return { text: THREAD_EXPIRED_TEXT, isEnded: true };
  const expiryText = formatJstDateTime(instant, {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return {
    text: `${expiryText} 頃落ちます(あと${formatRemainingDuration(remaining)})`,
    isEnded: false,
  };
}
